import type { BadgeProps } from "@/components/ui/badge";
import type {
  AdminOrderEntitlementState,
  OrderStatus,
  PaymentMethod,
  PaymentProvider,
  PaymentStatus,
} from "@/services/admin/orders.types";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

export function formatOrderCurrency(value: number, currency = "BRL"): string {
  if (value == null || Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  }
}

export function formatOrderDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

export function shortOrderId(orderId: string): string {
  if (orderId.length <= 12) return orderId;
  return `${orderId.slice(0, 8)}…`;
}

export function getOrderStatusLabel(status: OrderStatus): string {
  const labels: Record<OrderStatus, string> = {
    Draft: "Rascunho",
    PendingPayment: "Aguardando pagamento",
    Paid: "Pago",
    Cancelled: "Cancelado",
    Failed: "Falhou",
  };
  return labels[status] ?? status;
}

export function getOrderStatusVariant(status: OrderStatus): BadgeVariant {
  switch (status) {
    case "Paid":
      return "success";
    case "PendingPayment":
      return "warning";
    case "Failed":
    case "Cancelled":
      return "destructive";
    default:
      return "secondary";
  }
}

export function isEmphasizedOrderStatus(status: OrderStatus): boolean {
  return status === "PendingPayment" || status === "Failed";
}

export function getPaymentStatusLabel(status?: PaymentStatus | null): string {
  if (!status) return "Sem pagamento";
  const labels: Record<PaymentStatus, string> = {
    Created: "Criado",
    Pending: "Pendente",
    Authorized: "Autorizado",
    Captured: "Capturado",
    Failed: "Falhou",
    Cancelled: "Cancelado",
    Refunded: "Reembolsado",
  };
  return labels[status] ?? status;
}

export function getPaymentStatusVariant(status?: PaymentStatus | null): BadgeVariant {
  if (!status) return "outline";
  switch (status) {
    case "Captured":
    case "Authorized":
      return "success";
    case "Pending":
    case "Created":
      return "warning";
    case "Failed":
    case "Cancelled":
      return "destructive";
    default:
      return "secondary";
  }
}

export function getPaymentProviderLabel(provider?: PaymentProvider | null): string {
  if (!provider) return "—";
  const labels: Partial<Record<PaymentProvider, string>> = {
    MercadoPago: "Mercado Pago",
    Paypal: "PayPal",
    Stripe: "Stripe",
    Adyen: "Adyen",
    Internal: "Interno",
    Unknown: "Desconhecido",
  };
  return labels[provider] ?? provider;
}

export function getPaymentMethodLabel(method?: PaymentMethod | null): string {
  if (!method) return "—";
  const labels: Partial<Record<PaymentMethod, string>> = {
    Pix: "PIX",
    Card: "Cartão",
    PayPal: "PayPal",
    BankTransfer: "Transferência",
    None: "Nenhum",
    Unknown: "Desconhecido",
  };
  return labels[method] ?? method;
}

export function getEntitlementStateLabel(state: AdminOrderEntitlementState): string {
  const labels: Record<AdminOrderEntitlementState, string> = {
    Granted: "Concedido",
    Missing: "Ausente",
    NotExpected: "Não esperado",
    Unknown: "Desconhecido",
  };
  return labels[state] ?? state;
}

export function getEntitlementStateVariant(state: AdminOrderEntitlementState): BadgeVariant {
  switch (state) {
    case "Granted":
      return "success";
    case "Missing":
      return "destructive";
    case "Unknown":
      return "warning";
    default:
      return "secondary";
  }
}

export function formatCustomerDisplay(
  name?: string | null,
  email?: string | null
): string {
  const parts = [name, email].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Cliente indisponível";
}

export function formatStudentDisplay(
  name?: string | null,
  studentId?: string | null
): string {
  if (name && studentId) return `${name} (${studentId})`;
  if (name) return name;
  if (studentId) return studentId;
  return "Estudante indisponível";
}
